import { Link } from "react-router-dom";
import { useLang } from "../i18n";
import { useBoot } from "../components/Layout";
import { IMG } from "../content/images";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { FinalCTA } from "../components/FinalCTA";
import { Download, ArrowRight } from "lucide-react";
import { useState } from "react";
import { api } from "../lib/api";

const About = () => {
  const { lang, t, p } = useLang();
  const boot = useBoot();
  const [dlMsg, setDlMsg] = useState("");
  const team = boot?.team || [];
  const founder = team.find((m) => m.is_founder);

  const downloadProfile = async () => {
    setDlMsg("");
    try {
      const res = await api.get(`/public/documents/company_profile?locale=${lang}`);
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api${res.data.url.replace("/api", "")}`, "_blank");
    } catch {
      setDlMsg(lang === "fr"
        ? "Le profil d'entreprise sera disponible prochainement. Contactez info@bulatelecoms.com."
        : "The company profile will be available soon. Contact info@bulatelecoms.com.");
    }
  };

  return (
    <div data-testid="about-page">
      <Seo title={t.nav.about} path="a-propos" />
      <PageHero eyebrow="BULA TELECOMS SA" title={t.about.heading} image={IMG.teamWomen}
        crumbs={[{ label: t.nav.about }]} />

      <section className="bg-[#F5F5F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <Reveal className="bg-white rounded-2xl border border-[#101214]/10 p-8 lg:p-10 shadow-sm">
            <h2 className="text-xl sm:text-2xl font-bold text-[#101214]">{t.about.missionTitle}</h2>
            <p className="mt-4 text-base text-[#101214]/70 leading-relaxed">{t.about.mission}</p>
          </Reveal>
          <Reveal delay={120} className="bg-[#101214] rounded-2xl p-8 lg:p-10">
            <h2 className="text-xl sm:text-2xl font-bold text-white">{t.about.visionTitle}</h2>
            <p className="mt-4 text-base text-white/70 leading-relaxed">{t.about.vision}</p>
            <p className="mt-4 text-sm font-bold text-[#FF9900] leading-relaxed">{t.about.vision2}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24">
          <Reveal><h2 className="text-2xl sm:text-3xl font-black tracking-tight text-[#101214]">{t.about.valuesTitle}</h2></Reveal>
          <div className="mt-8 flex flex-wrap gap-3">
            {t.about.values.map((v, i) => (
              <Reveal key={v} delay={i * 50}>
                <span className="inline-block bg-[#FF6600]/10 text-[#FF6600] border border-[#FF6600]/25 font-bold text-sm rounded-full px-5 py-2.5">{v}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#08090A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24">
          <Reveal><h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white">{t.about.timelineTitle}</h2></Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
            {t.about.timeline.map((step, i) => (
              <Reveal key={step.year} delay={i * 120}>
                <div className="relative pl-6 border-l-2 border-[#FF6600]">
                  <span className="absolute -left-[7px] top-1 w-3 h-3 rounded-full bg-[#FF6600] shadow-[0_0_12px_rgba(255,102,0,0.7)]" />
                  <p className="font-mono text-[#FFC000] text-sm font-bold tracking-widest">{step.year}</p>
                  <h3 className="mt-2 text-lg font-bold text-white">{step.title}</h3>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {(founder || team.length > 0) && (
        <section className="bg-[#F5F5F3]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {team.map((m) => (
                <Reveal key={m.id} className="bg-white rounded-2xl border border-[#101214]/10 p-6 text-center">
                  {m.photo_url
                    ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${m.photo_url}`} alt={m.name} loading="lazy" className="w-24 h-24 rounded-full object-cover mx-auto border-2 border-[#FF6600]/30" />
                    : <span className="w-24 h-24 rounded-full bg-[#101214] text-[#FF6600] font-black text-2xl flex items-center justify-center mx-auto">{m.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}</span>}
                  <h3 className="mt-4 font-bold text-[#101214]">{m.name}</h3>
                  <p className="text-sm text-[#FF6600] font-bold">{lang === "fr" ? m.role_fr : m.role_en}</p>
                  {(lang === "fr" ? m.bio_fr : m.bio_en) && <p className="mt-3 text-sm text-[#101214]/60 leading-relaxed">{lang === "fr" ? m.bio_fr : m.bio_en}</p>}
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-white" data-testid="corporate-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24">
          <Reveal><h2 className="text-2xl sm:text-3xl font-black tracking-tight text-[#101214]">{t.about.corporateTitle}</h2></Reveal>
          <ul className="mt-8 space-y-4 max-w-3xl">
            {t.about.corporate.map((c) => (
              <li key={c.slice(0, 24)} className="flex gap-3 text-sm sm:text-base text-[#101214]/70 leading-relaxed">
                <span className="w-2 h-2 rounded-full bg-[#FF6600] shrink-0 mt-2" />{c}
              </li>
            ))}
          </ul>
          <Reveal className="mt-10 flex flex-wrap gap-3">
            <button onClick={downloadProfile} data-testid="download-profile-btn"
              className="inline-flex items-center gap-2 bg-[#101214] hover:bg-[#FF6600] text-white hover:text-[#08090A] font-black rounded-full px-6 py-3 text-sm transition-colors">
              <Download size={16} /> {t.about.downloadProfile}
            </button>
            <Link to={p("contact")} data-testid="partnership-cta"
              className="inline-flex items-center gap-2 border border-[#101214]/20 hover:border-[#FF6600] text-[#101214] font-bold rounded-full px-6 py-3 text-sm transition-colors">
              {t.about.partnershipCta} <ArrowRight size={15} />
            </Link>
            <Link to={p("contact")} data-testid="media-contact-link" className="inline-flex items-center gap-2 text-sm font-bold text-[#FF6600] px-2 py-3">
              {t.about.mediaContact}
            </Link>
          </Reveal>
          {dlMsg && <p className="mt-4 text-sm text-[#101214]/60" role="status" data-testid="download-profile-status">{dlMsg}</p>}
        </div>
      </section>

      <FinalCTA />
    </div>
  );
};

export default About;
